import React, { useEffect, useState } from "react";
import { Printer, Plus, Trash2, ExternalLink, CheckCircle2, MessageSquare, RotateCcw, Send, Save, Edit2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

/**
 * Printables tab — visible to Admin/RPM/PM (with edit) and Designer (submission).
 * - Admin/RPM/PM add rows with sample link.
 * - Designer submits their design link + marks complete.
 * - Admin can leave remarks or reassign back to Designer.
 * - RPM/PM read-only view of submissions/remarks.
 */
export default function PrintablesTab({ event }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", sample_link: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [openThread, setOpenThread] = useState(null);

  const canManage = ["admin", "regional_pm", "project_manager"].includes(user.role);
  const isDesigner = user.role === "designer";
  const isAdmin = user.role === "admin";

  const load = async () => {
    const r = await api.get(`/events/${event.event_id}/printables`);
    setRows(r.data || []);
  };
  useEffect(() => { load(); }, [event.event_id]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post(`/events/${event.event_id}/printables`, form);
      toast.success("Printable added");
      setForm({ name: "", description: "", sample_link: "" });
      setShowAdd(false);
      load();
    } catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Submission history will be wiped.`)) return;
    await api.delete(`/printables/${p.printable_id}`);
    load();
  };

  // Progress = fraction with status=complete
  const total = rows.length;
  const complete = rows.filter((r) => r.submission?.status === "complete").length;
  const pct = total ? Math.round((complete / total) * 100) : 0;

  return (
    <div data-testid="printables-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-xl flex items-center gap-2"><Printer className="w-5 h-5 text-orange-600" /> Designs</h3>
          <p className="text-sm text-slate-500">
            {canManage && "Add every collateral (posters, invites, banners…). Designer will submit finished links here."}
            {isDesigner && "Submit your design link against each item. Admin will review & mark reassigned or complete."}
          </p>
        </div>
        {canManage && <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-printable-btn"><Plus className="w-4 h-4" /> Add printable</button>}
      </div>

      {total > 0 && (
        <div className="dashboard-card p-4 mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold uppercase text-slate-500">Progress</span>
            <span className="text-xs font-bold text-orange-700">{complete} / {total} complete · {pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full brand-gradient transition-all duration-500" style={{ width: `${pct}%` }} data-testid="printable-progress" />
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rows.length === 0 && <div className="dashboard-card p-12 text-center text-slate-400">No designs yet. Add one so designers can start work.</div>}
        {rows.map((p) => (
          <PrintableRow key={p.printable_id} printable={p} onOpenThread={() => setOpenThread(p)}
            userRole={user.role} onDelete={() => del(p)} reload={load} />
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={add} className="bg-white rounded-2xl p-6 max-w-md w-full space-y-3">
            <h3 className="font-display font-black text-xl">Add printable</h3>
            <input required placeholder="Name (e.g. Delegate Poster)" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="printable-name-input" />
            <input placeholder="Sample link (reference for Designer)" className="input-pill" value={form.sample_link} onChange={(e) => setForm({ ...form, sample_link: e.target.value })} data-testid="printable-sample-input" />
            <textarea rows={3} placeholder="Description / brief" className="input-pill" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost flex-1 py-2.5">Cancel</button>
              <button type="submit" className="btn-primary flex-1 py-2.5" data-testid="printable-submit">Add</button>
            </div>
          </form>
        </div>
      )}

      {openThread && <RemarkThread printable={openThread} onClose={() => setOpenThread(null)} onReload={load} isAdmin={isAdmin} isDesigner={isDesigner} />}
    </div>
  );
}

function PrintableRow({ printable, onOpenThread, userRole, onDelete, reload }) {
  const isDesigner = userRole === "designer";
  const isAdmin = userRole === "admin";
  const canManage = ["admin", "regional_pm", "project_manager"].includes(userRole);
  const [editing, setEditing] = useState(false);
  const [linkForm, setLinkForm] = useState("");
  const [labelForm, setLabelForm] = useState("");
  const sub = printable.submission;

  const submitLink = async () => {
    if (!linkForm.trim()) return toast.error("Link required");
    try {
      await api.post(`/printables/${printable.printable_id}/submit-link`, { link: linkForm.trim(), label: labelForm.trim() });
      toast.success("Link added");
      setLinkForm(""); setLabelForm("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markComplete = async () => {
    try {
      await api.post(`/printables/${printable.printable_id}/mark-complete`, { completed: true });
      toast.success("Marked complete");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reassign = async () => {
    const reason = window.prompt("Send back to Designer — reason / changes required:");
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/printables/${printable.printable_id}/reassign`, { reason: reason.trim() });
      toast.success("Reassigned to designer");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const statusChip = {
    complete: <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Complete ✓</span>,
    submitted: <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-bold">Submitted</span>,
    reassigned: <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold">Reassigned</span>,
  }[sub?.status];

  return (
    <div className="dashboard-card p-4" data-testid={`printable-${printable.printable_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-slate-900">{printable.name}</div>
            {statusChip}
          </div>
          {printable.description && <div className="text-xs text-slate-500 mt-0.5">{printable.description}</div>}
          <div className="flex flex-wrap gap-3 mt-2 text-xs">
            {printable.sample_link && (
              <a href={printable.sample_link} target="_blank" rel="noreferrer" className="text-slate-600 hover:text-orange-600 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Sample</a>
            )}
            {(sub?.links?.length > 0 || sub?.link) && (
              <div className="text-orange-700 font-semibold inline-flex items-center gap-1" data-testid={`submission-link-${printable.printable_id}`}>
                <ExternalLink className="w-3 h-3" /> {(sub?.links?.length || (sub?.link ? 1 : 0))} link{(sub?.links?.length || 1) > 1 ? "s" : ""} submitted
              </div>
            )}
            {(sub?.remarks?.length || 0) > 0 && (
              <button onClick={onOpenThread} className="text-fuchsia-700 font-semibold hover:underline inline-flex items-center gap-1" data-testid={`thread-btn-${printable.printable_id}`}>
                <MessageSquare className="w-3 h-3" /> {sub.remarks.length} remark{sub.remarks.length > 1 ? "s" : ""}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(isAdmin || isDesigner) && sub && (
            <button onClick={onOpenThread} className="p-1.5 rounded hover:bg-fuchsia-50 text-fuchsia-600" title="Open thread" data-testid={`open-thread-${printable.printable_id}`}><MessageSquare className="w-4 h-4" /></button>
          )}
          {isAdmin && sub?.link && sub?.status !== "reassigned" && (
            <button onClick={reassign} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Send back for changes" data-testid={`reassign-${printable.printable_id}`}><RotateCcw className="w-4 h-4" /></button>
          )}
          {canManage && <button onClick={onDelete} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Existing submitted links (visible to everyone) */}
      {(sub?.links?.length > 0 || sub?.link) && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`design-links-${printable.printable_id}`}>
          <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Submitted links</div>
          <div className="space-y-1">
            {(sub?.links || (sub?.link ? [{ link_id: "legacy", label: "Submission", link: sub.link, submitted_at: sub.submitted_at }] : [])).map((l) => (
              <div key={l.link_id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2 py-1.5">
                <span className="font-semibold text-slate-700 truncate max-w-[140px]">{l.label}</span>
                <a href={l.link} target="_blank" rel="noreferrer" className="text-orange-700 hover:underline truncate flex-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {l.link}</a>
                {isDesigner && l.link_id !== "legacy" && (
                  <button onClick={async () => { await api.delete(`/printables/${printable.printable_id}/submit-link/${l.link_id}`); reload(); }} className="text-rose-500 p-0.5" data-testid={`del-link-${l.link_id}`}><Trash2 className="w-3 h-3" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isDesigner && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Submit a new design link</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input placeholder="Label (e.g. Poster v2, Reel cover)" className="input-pill py-2 text-sm w-full sm:w-48" value={labelForm} onChange={(e) => setLabelForm(e.target.value)} data-testid={`label-input-${printable.printable_id}`} />
            <input placeholder="https://…" className="input-pill py-2 text-sm flex-1" value={linkForm} onChange={(e) => setLinkForm(e.target.value)} data-testid={`link-input-${printable.printable_id}`} />
            <button onClick={submitLink} className="btn-primary py-2 text-sm" data-testid={`submit-link-${printable.printable_id}`}><Send className="w-3 h-3" /> Add link</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {!sub?.completed && (sub?.links?.length || sub?.link) && (
              <button onClick={markComplete} className="btn-primary py-1.5 text-xs" data-testid={`mark-complete-${printable.printable_id}`}><CheckCircle2 className="w-3 h-3" /> Mark this design complete</button>
            )}
            {sub?.completed && (
              <span className="text-[11px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">✓ Marked complete on {sub.completed_at?.slice(0, 10)}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RemarkThread({ printable, onClose, onReload, isAdmin, isDesigner }) {
  const [text, setText] = useState("");
  const [sub, setSub] = useState(printable.submission);
  const canPost = isAdmin || isDesigner;

  const post = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/printables/${printable.printable_id}/remark`, { text: text.trim() });
      setText("");
      // Reload thread
      const r = await api.get(`/events/${printable.event_id}/printables`);
      const fresh = (r.data || []).find((x) => x.printable_id === printable.printable_id);
      setSub(fresh?.submission);
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="printable-thread">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="font-display font-black text-lg">{printable.name} — Thread</h3>
          <p className="text-xs text-slate-500">Conversation between Admin & Designer only.</p>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {(sub?.remarks || []).length === 0 && <p className="text-sm text-slate-400 text-center py-6">No remarks yet.</p>}
          {(sub?.remarks || []).map((r) => (
            <div key={r.remark_id} className={`p-3 rounded-xl ${r.author_role === "admin" ? "bg-orange-50" : "bg-fuchsia-50"}`}>
              <div className="text-xs font-bold text-slate-700">{r.author_name} · <span className="text-slate-400">{r.author_role}</span></div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{r.text}</div>
              <div className="text-[10px] text-slate-400 mt-1">{new Date(r.at).toLocaleString()}</div>
            </div>
          ))}
        </div>
        {canPost ? (
          <div className="p-4 border-t border-slate-100">
            <div className="flex gap-2">
              <input placeholder="Type a remark…" className="input-pill flex-1" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && post()} data-testid="remark-input" />
              <button onClick={post} className="btn-primary" data-testid="remark-send"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        ) : (
          <div className="p-3 text-xs text-slate-400 text-center border-t border-slate-100 bg-slate-50">Read-only — remarks are between Admin & Designer only.</div>
        )}
      </div>
    </div>
  );
}
